// components/TeamMemberForm.js
import { useState, useEffect } from "react";
import { teamMemberService } from "../services/apiService";

const TeamMemberForm = ({ teamMember, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    designation: "",
    profileSummary: "",
    linkedIn: "",
    anyMoreLink: "",
    detailInfo: "",
    status: "current",
  });
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // const [previewImage, setPreviewImage] = useState(null); // Reserved for future preview feature

  useEffect(() => {
    if (teamMember) {
      setFormData({
        name: teamMember.name,
        location: teamMember.location,
        designation: teamMember.designation,
        profileSummary: teamMember.profileSummary,
        linkedIn: teamMember.linkedIn,
        anyMoreLink: teamMember.anyMoreLink || "",
        detailInfo: teamMember.detailInfo,
        status: teamMember.status || "current",
      });
      // Reserved for future preview feature
      // setPreviewImage(
      //   teamMember.image ? `data:image/jpeg;base64,${teamMember.image}` : null
      // );
    }
  }, [teamMember]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please upload an image file (JPEG, PNG, etc.)");
        return;
      }

      // Reserved for future preview feature
      // const reader = new FileReader();
      // reader.onloadend = () => {
      //   setPreviewImage(reader.result);
      // };
      // reader.readAsDataURL(file);

      setImageFile(file);
      setError("");
    }
  };

  const validateLinkedIn = (url) => {
    return /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub|company)\/[^\s]+\/?$/.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const requiredFields = [
        "name",
        "location",
        "designation",
        "profileSummary",
        "linkedIn",
        "detailInfo",
      ];
      const missingFields = requiredFields.filter(
        (field) => !formData[field]?.trim()
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      if (!validateLinkedIn(formData.linkedIn)) {
        throw new Error("Please enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/username)");
      }

      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value || "");
      });

      if (imageFile) {
        formDataToSend.append("image", imageFile);
      } else if (!teamMember?.image) {
        throw new Error("Please select an image for new team members");
      }

      if (teamMember) {
        await teamMemberService.updateTeamMember(teamMember._id, formDataToSend);
      } else {
        await teamMemberService.createTeamMember(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error("Submission error:", err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save team member. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {error && <div className="p-2 text-red-600 bg-red-100 rounded">{error}</div>}

      <div>
        <label className="block font-medium">
          {teamMember ? "Update Image" : "Upload Image*"}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="block w-full mt-1 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#166534] file:text-white"
          required={!teamMember}
        />
      </div>

      {teamMember?.image && (
        <div className="mb-4">
          <img
            src={`data:image/jpeg;base64,${teamMember.image}`}
            className="h-24 w-24 object-cover rounded border"
            alt="Current team member"
          />
        </div>
      )}

      <div>
        <label className="block font-medium">Name*</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <label className="block font-medium">Location*</label>
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={handleChange}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <label className="block font-medium">Designation*</label>
        <input
          type="text"
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <label className="block font-medium">Employment Status*</label>
        <div className="flex gap-4 mt-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="status"
              value="current"
              checked={formData.status === "current"}
              onChange={handleChange}
              className="w-4 h-4 text-[#166534] focus:ring-[#166534]"
            />
            <span className="ml-2 text-gray-700">Current Employee</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="status"
              value="former"
              checked={formData.status === "former"}
              onChange={handleChange}
              className="w-4 h-4 text-[#166534] focus:ring-[#166534]"
            />
            <span className="ml-2 text-gray-700">Former Employee / Alumnus</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block font-medium">Profile Summary*</label>
        <textarea
          name="profileSummary"
          value={formData.profileSummary}
          onChange={handleChange}
          rows={3}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div>
        <label className="block font-medium">LinkedIn URL* (e.g., https://www.linkedin.com/in/username)</label>
        <input
          type="url"
          name="linkedIn"
          value={formData.linkedIn}
          onChange={handleChange}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
          placeholder="https://www.linkedin.com/in/username"
        />
      </div>

      <div>
        <label className="block font-medium">Any More Link</label>
        <input
          type="url"
          name="anyMoreLink"
          value={formData.anyMoreLink}
          onChange={handleChange}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          placeholder="https://example.com"
        />
      </div>

      <div>
        <label className="block font-medium">Detailed Work*</label>
        <textarea
          name="detailInfo"
          value={formData.detailInfo}
          onChange={handleChange}
          rows={5}
          className="w-full p-2 mt-1 border rounded focus:ring-[#166534] focus:border-[#166534]"
          required
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-white bg-[#166534] rounded hover:bg-[#166534]/90 disabled:opacity-50"
        >
          {isLoading ? "Saving..." : teamMember ? "Update" : "Create"}
        </button>
      </div>
    </form>
  );
};

export default TeamMemberForm;